import React, { useState, useEffect, useCallback } from 'react';
import { Question } from '../types';
import { fetchNextQuestion } from '../lib/api';
import { TriageCard } from '../components/TriageCard';
import { RefreshCw } from 'lucide-react';

export const TriagePage: React.FC = () => {
  const [question, setQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadQuestion = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = await fetchNextQuestion();
      if (!q) {
        setError('Nenhuma questão cadastrada para triagem.');
      } else {
        setQuestion(q);
      }
    } catch {
      setError('Falha ao conectar com a base de triagem.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-paper-50 px-4 py-8 dark:bg-study-slate sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Triagem Rápida de Questões
            </h1>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              Identifique dados, incógnita, gatilho conceitual e fórmula-ponte em até 3 minutos.
            </p>
          </div>

          <button
            type="button"
            onClick={loadQuestion}
            title="Pular / Nova Questão"
            className="flex items-center gap-1.5 rounded border border-paper-300 bg-paper-100 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-paper-200 dark:border-study-line dark:bg-study-card dark:text-zinc-300 dark:hover:bg-study-line transition"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Pular / Nova
          </button>
        </div>

        {loading ? (
          <div className="flex h-72 items-center justify-center rounded-lg border border-paper-300 bg-paper-100 dark:border-study-line dark:bg-study-panel">
            <div className="text-center">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-zinc-400 border-t-zinc-800 dark:border-zinc-600 dark:border-t-zinc-200" />
              <p className="mt-3 font-mono text-xs text-zinc-500 dark:text-zinc-400">
                Ponderando edital e sorteando questão...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 text-center text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-300">
            <p className="font-semibold">{error}</p>
            <button
              type="button"
              onClick={loadQuestion}
              className="mt-3 inline-block rounded bg-zinc-900 px-4 py-1.5 text-xs font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
            >
              Tentar Novamente
            </button>
          </div>
        ) : question ? (
          <TriageCard question={question} onNextQuestion={loadQuestion} />
        ) : null}
      </div>
    </div>
  );
};
